import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@blueprintjs/core';
import { DateInput } from '@blueprintjs/datetime';
import moment from 'moment';
import { RRule, rrulestr } from 'rrule';
import { Choice } from 'components/FormBits';

// How often a service agreement's visits repeat. Replaces react-rrule-generator
// in the agreement form. It writes the same thing the generator wrote: an
// RRule string ("DTSTART:…\nRRULE:FREQ=…;INTERVAL=…;BYDAY=…;UNTIL=…"), built
// by the same `rrule` library with the options in the same order (start,
// repeat, end), which is what the API parses (RecurringScheduleGenerator).
//
// Monthly and yearly choices come from the start date ("on the 16th" / "on the
// 3rd Friday"), so there are no extra pickers. Under the controls, the next
// visits are shown as dates, so the result can be checked before saving.
// `readOnly` (editing an agreement) shows the rule and its next visits only.
// `children` (the visit times) sit just above the preview.
// Styles: .rp-* in styles/job-form.scss.

const WEEKDAYS = [
  { value: 0, short: 'M', label: 'Monday' },
  { value: 1, short: 'T', label: 'Tuesday' },
  { value: 2, short: 'W', label: 'Wednesday' },
  { value: 3, short: 'T', label: 'Thursday' },
  { value: 4, short: 'F', label: 'Friday' },
  { value: 5, short: 'S', label: 'Saturday' },
  { value: 6, short: 'S', label: 'Sunday' },
];
const FREQ = { weekly: RRule.WEEKLY, monthly: RRule.MONTHLY, yearly: RRule.YEARLY };
const UNIT = { weekly: 'week', monthly: 'month', yearly: 'year' };
const ORDINAL = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', '-1': 'last' };

// moment weekday (0 = Sunday) -> rrule weekday (0 = Monday)
const rruleDay = (date) => (moment(date).isoWeekday() + 6) % 7;
const nthOfMonth = (date) => Math.ceil(moment(date).date() / 7);
const isLastOfMonth = (date) => moment(date).add(7, 'days').month() !== moment(date).month();
const ordinalDay = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

// The rule in words, e.g. "Every 2 weeks on Monday and Thursday".
export const describeRule = (rrule) => {
  if (!rrule) return null;
  try {
    const { dtstart, until, ...rest } = rrulestr(String(rrule).replace(' RRULE:', '\nRRULE:')).origOptions;
    let text = capitalise(new RRule(rest).toText());
    // rrule can't say "the third Friday" (BYSETPOS); say it ourselves.
    const position = [].concat(rest.bysetpos || [])[0];
    const weekday = [].concat(rest.byweekday || [])[0];
    if (position && weekday !== undefined) {
      const dayIndex = typeof weekday === 'number' ? weekday : weekday.weekday;
      const interval = rest.interval || 1;
      const yearly = rest.freq === RRule.YEARLY;
      const every = yearly
        ? (interval === 1 ? 'Every year' : `Every ${interval} years`)
        : (interval === 1 ? 'Every month' : `Every ${interval} months`);
      const month = yearly && rest.bymonth ? ` of ${moment().month([].concat(rest.bymonth)[0] - 1).format('MMMM')}` : '';
      text = `${every} on the ${ORDINAL[position]} ${WEEKDAYS[dayIndex].label}${month}`;
    }
    if (dtstart) text += `, from ${moment(dtstart).format('ll')}`;
    if (until) text += ` until ${moment(until).format('ll')}`;
    return text;
  } catch (error) {
    return null;
  }
};

// The next `count` visits of a rule (from today, or from its start if later),
// stopping at `before` when given.
const nextVisits = (rrule, count = 4, before = null) => {
  try {
    const rule = rrulestr(String(rrule).replace(' RRULE:', '\nRRULE:'));
    const from = moment().startOf('day').toDate();
    // rrule repeats in fixed UTC steps; keep each visit at the start's local
    // time across daylight-saving changes (else a visit set for midnight
    // shows on the day before once the clocks go back).
    const startOffset = rule.options.dtstart ? moment(rule.options.dtstart).utcOffset() : null;
    const local = (date) =>
      startOffset === null ? date : moment(date).add(startOffset - moment(date).utcOffset(), 'minutes').toDate();
    const visits = [];
    rule.all((raw, index) => {
      const date = local(raw);
      if (before && date >= before) return false;
      if (date >= from) visits.push(date);
      return visits.length < count && index < 2000;
    });
    return visits;
  } catch (error) {
    return [];
  }
};

const hasEnd = (rrule) => {
  try {
    const { until, count } = rrulestr(String(rrule).replace(' RRULE:', '\nRRULE:')).origOptions;
    return Boolean(until || count);
  } catch (error) {
    return false;
  }
};

const LIST_LIMIT = 100; // most visits listed for a schedule with an end
const OPEN_ENDED_MONTHS = 12; // a schedule that never ends lists the next year

// The rule in words, the next visits as date tiles, and (folded away) a list
// of the visits: all of them when the schedule ends, else the next 12 months.
export class VisitPreview extends Component {
  state = { open: false };

  render() {
    const { rrule, startTime, endTime } = this.props;
    const { open } = this.state;
    const summary = describeRule(rrule);
    const times = startTime && endTime ? `${moment(startTime).format('h:mm A')} – ${moment(endTime).format('h:mm A')}` : null;
    const tiles = rrule ? nextVisits(rrule, 4) : [];
    const ends = rrule && hasEnd(rrule);
    const list = rrule
      ? nextVisits(rrule, ends ? LIST_LIMIT + 1 : 400, ends ? null : moment().add(OPEN_ENDED_MONTHS, 'months').toDate())
      : [];
    const more = ends && list.length > LIST_LIMIT;
    const listed = more ? list.slice(0, LIST_LIMIT) : list;
    const toggleLabel = ends
      ? `${open ? 'Hide' : 'Show'} all ${more ? `${LIST_LIMIT}+` : listed.length} visits`
      : `${open ? 'Hide' : 'Show'} visits in the next ${OPEN_ENDED_MONTHS} months (${listed.length})`;

    // Rows grouped by month.
    const months = [];
    listed.forEach((date) => {
      const key = moment(date).format('MMMM YYYY');
      const last = months[months.length - 1];
      if (last && last.key === key) last.dates.push(date);
      else months.push({ key, dates: [date] });
    });

    return (
      <div className="rp-preview" aria-live="polite">
        <p className="rp-preview__summary">
          <Icon icon="repeat" iconSize={14} />
          <span>{summary ? `${summary}${times ? `, ${times}` : ''}.` : 'This repeat can’t be shown here.'}</span>
        </p>
        {tiles.length > 0 ? (
          <div className="rp-visits">
            <span className="rp-visits__label">Next visits</span>
            <ol className="rp-visits__list">
              {tiles.map((date) => (
                <li key={date.getTime()} className="rp-visit" title={moment(date).format('dddd, LL')}>
                  <span className="rp-visit__weekday">{moment(date).format('ddd')}</span>
                  {/* Day and month together, so visits on the same day of different months stand apart. */}
                  <span className="rp-visit__day">{moment(date).format('D MMM')}</span>
                  <span className="rp-visit__year">{moment(date).format('YYYY')}</span>
                </li>
              ))}
            </ol>
            {listed.length > tiles.length && (
              <React.Fragment>
                <button
                  type="button"
                  className="rp-all__toggle"
                  aria-expanded={open}
                  onClick={() => this.setState({ open: !open })}
                >
                  <Icon icon={open ? 'chevron-down' : 'chevron-right'} iconSize={14} />
                  {toggleLabel}
                </button>
                {open && (
                  <div className="rp-all">
                    {months.map((month) => (
                      <section key={month.key} className="rp-all__month">
                        <h4 className="rp-all__month-title">
                          {month.key}
                          <span className="rp-all__count">{month.dates.length}</span>
                        </h4>
                        <ol className="rp-all__list">
                          {month.dates.map((date) => (
                            <li key={date.getTime()} className="rp-all__row">
                              <span className="rp-all__date">{moment(date).format('ddd, MMM D')}</span>
                              {times && <span className="rp-all__time">{times}</span>}
                            </li>
                          ))}
                        </ol>
                      </section>
                    ))}
                    {more && <p className="rp-all__more">The schedule goes on after these.</p>}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        ) : (
          summary && <p className="rp-preview__none">No visits left on this schedule.</p>
        )}
      </div>
    );
  }
}

VisitPreview.propTypes = {
  rrule: PropTypes.string,
  startTime: PropTypes.any,
  endTime: PropTypes.any,
};

class RepeatPicker extends Component {
  constructor(props) {
    super(props);
    this.state = this.stateFromValue(props.value);
  }

  componentDidMount() {
    // A new agreement starts with a usable rule (weekly on today), not nothing.
    if (!this.props.readOnly && !this.props.value) this.emit(this.state);
  }

  componentDidUpdate(prevProps) {
    // The visit start time changed: keep the rule's start in step with it.
    if (!this.props.readOnly && this.props.startTime !== prevProps.startTime && this.state.parsed) {
      this.emit(this.state);
    }
  }

  // Read an existing rule into the controls; anything unreadable starts fresh.
  stateFromValue(value) {
    const today = moment().startOf('day').toDate();
    const fresh = {
      parsed: true,
      freq: 'weekly',
      interval: 1,
      days: [rruleDay(today)],
      monthlyMode: 'day',
      start: today,
      ends: 'never',
      until: moment(today).add(1, 'year').toDate(),
    };
    if (!value) return fresh;
    try {
      const options = rrulestr(String(value).replace(' RRULE:', '\nRRULE:')).origOptions;
      const freq = Object.keys(FREQ).find((key) => FREQ[key] === options.freq);
      if (!freq) return { ...fresh, parsed: false };
      const byweekday = [].concat(options.byweekday || []).map((day) => (typeof day === 'number' ? day : day.weekday));
      return {
        parsed: true,
        freq,
        interval: options.interval || 1,
        days: freq === 'weekly' && byweekday.length ? byweekday : fresh.days,
        monthlyMode: options.bysetpos ? 'nth' : 'day',
        start: options.dtstart ? moment(options.dtstart).startOf('day').toDate() : today,
        ends: options.until ? 'on' : 'never',
        until: options.until || fresh.until,
      };
    } catch (error) {
      return { ...fresh, parsed: false };
    }
  }

  // Same option names and order as react-rrule-generator's computeRRule.
  buildRule(state) {
    const { freq, interval, days, monthlyMode, start, ends, until } = state;
    const time = this.props.startTime ? moment(this.props.startTime) : moment();
    const dtstart = moment(start).hours(time.hours()).minutes(time.minutes()).seconds(0).milliseconds(0).toDate();
    const options = { dtstart, freq: FREQ[freq], interval };
    if (freq === 'weekly') {
      options.byweekday = [...days].sort();
    } else if (monthlyMode === 'nth') {
      options.bysetpos = isLastOfMonth(start) ? -1 : nthOfMonth(start);
      options.byweekday = [rruleDay(start)];
      if (freq === 'yearly') options.bymonth = moment(start).month() + 1;
    } else {
      options.bymonthday = moment(start).date();
      if (freq === 'yearly') options.bymonth = moment(start).month() + 1;
    }
    if (ends === 'on' && until) options.until = moment(until).endOf('day').toDate();
    return new RRule(options).toString();
  }

  emit(state) {
    this.props.onChange(this.buildRule(state));
  }

  update(changes) {
    const next = { ...this.state, ...changes, parsed: true };
    if (next.freq === 'weekly' && next.days.length === 0) next.days = [rruleDay(next.start)];
    this.setState(next);
    this.emit(next);
  }

  toggleDay(day) {
    const { days } = this.state;
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    if (next.length) this.update({ days: next });
  }

  renderVisits() {
    const { value, startTime, endTime } = this.props;
    return <VisitPreview rrule={value} startTime={startTime} endTime={endTime} />;
  }

  render() {
    const { readOnly } = this.props;
    if (readOnly) {
      return (
        <div className="rp rp--readonly">
          {this.props.children}
          {this.renderVisits()}
          <p className="rp-note">The repeat is set when the agreement is created and can’t be changed here.</p>
        </div>
      );
    }

    const { freq, interval, days, monthlyMode, start, ends, until, parsed } = this.state;
    const nth = isLastOfMonth(start) ? -1 : nthOfMonth(start);
    const weekdayName = moment(start).format('dddd');
    const onDay = freq === 'yearly' ? `On ${moment(start).format('MMMM D')}` : `On the ${ordinalDay(moment(start).date())}`;
    const onNth = freq === 'yearly'
      ? `On the ${ORDINAL[nth]} ${weekdayName} of ${moment(start).format('MMMM')}`
      : `On the ${ORDINAL[nth]} ${weekdayName}`;

    return (
      <div className="rp">
        {!parsed && (
          <p className="rp-note">The saved repeat can’t be edited with these options. Changing anything below replaces it.</p>
        )}

        <div className="rp-head">
          <Choice
            label="Repeats"
            value={freq}
            onChange={(value) => this.update({ freq: value })}
            options={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />
          <div className="rp-row">
            <span className="fb-label" id="rp-every-label">Every</span>
            <div className="rp-stepper" role="group" aria-labelledby="rp-every-label">
              <button
                type="button"
                className="rp-stepper__button"
                aria-label={`Fewer ${UNIT[freq]}s between visits`}
                disabled={interval <= 1}
                onClick={() => this.update({ interval: Math.max(1, interval - 1) })}
              >
                <Icon icon="minus" iconSize={14} />
              </button>
              <span className="rp-stepper__value">
                {interval === 1 ? UNIT[freq] : `${interval} ${UNIT[freq]}s`}
              </span>
              <button
                type="button"
                className="rp-stepper__button"
                aria-label={`More ${UNIT[freq]}s between visits`}
                disabled={interval >= 52}
                onClick={() => this.update({ interval: Math.min(52, interval + 1) })}
              >
                <Icon icon="plus" iconSize={14} />
              </button>
            </div>
          </div>
        </div>

        {freq === 'weekly' ? (
          <div className="rp-row">
            <span className="fb-label" id="rp-days-label">On</span>
            <div className="rp-days" role="group" aria-labelledby="rp-days-label">
              {WEEKDAYS.map((day) => {
                const on = days.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    className={`rp-day${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    aria-label={day.label}
                    title={day.label}
                    onClick={() => this.toggleDay(day.value)}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <Choice
            label="On"
            value={monthlyMode}
            onChange={(value) => this.update({ monthlyMode: value })}
            options={[
              { value: 'day', label: onDay },
              { value: 'nth', label: onNth },
            ]}
          />
        )}

        <div className="rp-dates">
          <div className="rp-date">
            <label className="fb-label" htmlFor="rp-start">Starts</label>
            <DateInput
              inputProps={{ id: 'rp-start' }}
              value={start}
              onChange={(date) => date && this.update({ start: moment(date).startOf('day').toDate() })}
              formatDate={(date) => moment(date).format('ll')}
              parseDate={(text) => (moment(text, 'll').isValid() ? moment(text, 'll').toDate() : null)}
              canClearSelection={false}
              popoverProps={{ minimal: true }}
            />
          </div>
          <div className="rp-date rp-date--ends">
            <Choice
              label="Ends"
              value={ends}
              onChange={(value) => this.update({ ends: value })}
              options={[
                { value: 'never', label: 'Never' },
                { value: 'on', label: 'On a date' },
              ]}
            />
            {ends === 'on' && (
              <DateInput
                inputProps={{ 'aria-label': 'Last day of the agreement' }}
                value={until}
                minDate={start}
                maxDate={moment(start).add(10, 'years').toDate()}
                onChange={(date) => date && this.update({ until: date })}
                formatDate={(date) => moment(date).format('ll')}
                parseDate={(text) => (moment(text, 'll').isValid() ? moment(text, 'll').toDate() : null)}
                canClearSelection={false}
                popoverProps={{ minimal: true }}
              />
            )}
          </div>
        </div>

        {this.props.children}
        {this.renderVisits()}
      </div>
    );
  }
}

RepeatPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  startTime: PropTypes.any,
  endTime: PropTypes.any,
  readOnly: PropTypes.bool,
  children: PropTypes.node, // shown above the preview (e.g. the visit times)
};

export default RepeatPicker;
